/**
 * Quadrant Component
 * Displays a quadrant with its title, task list, and configuration options
 * Handles drag-and-drop for task reordering and moving between quadrants
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreHorizontal, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { TaskItem } from './TaskItem';
import { QuadrantConfig } from './QuadrantConfig';

const PRESET_COLORS = ['color-red', 'color-orange', 'color-green', 'color-blue', 'color-purple'];

export function Quadrant({
  quadrant,
  openConfigId,
  dragOverQuadrantId,
  dropPosition,
  draggedTask,
  editingTask,
  inputs,
  setInputs,
  taskListRefs,
  onToggleConfig,
  onUpdateQuadrant,
  onToggleTask,
  onRemoveTask,
  onAddTask,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onEditStart,
  onEditChange,
  onEditSave,
  className,
  onTogglePlanToday,
}) {
  const [showCompleted, setShowCompleted] = React.useState(false);
  const tasks = quadrant.tasks || [];
  const activeTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  return (
    <motion.div layout key={quadrant.id} data-quadrant-id={quadrant.id} className={`quadrant ${quadrant.color} ${className || ''}`}>
      <div className="quadrant-header">
        <span className="quadrant-title">{quadrant.title}</span>
        <div className="quadrant-actions">
          <span className="quadrant-count">
            {activeTasks.length} task
            {activeTasks.length === 1 ? '' : 's'}
          </span>
          <button
            className="config-btn"
            onClick={() => onToggleConfig(quadrant.id)}
            title="Quadrant options"
          >
            <MoreHorizontal size={18} />
          </button>
          {openConfigId === quadrant.id && (
            <QuadrantConfig
              quadrant={quadrant}
              presetColors={PRESET_COLORS}
              inputs={inputs}
              onUpdateTitle={(newTitle) => {
                if (newTitle.trim() && newTitle !== quadrant.title) {
                  onUpdateQuadrant(quadrant.id, 'title', newTitle);
                }
                onToggleConfig(null);
              }}
              onUpdateColor={(color) => {
                onUpdateQuadrant(quadrant.id, 'color', color);
                onToggleConfig(null);
              }}
              onTitleInputChange={(value) => {
                // Update inputs for title editing
              }}
            />
          )}
        </div>
      </div>

      <div
        className={dragOverQuadrantId === quadrant.id ? 'task-list drag-over' : 'task-list'}
        ref={(el) => (taskListRefs.current[quadrant.id] = el)}
        onDragOver={onDragOver}
        onDragEnter={() => onDragEnter(quadrant.id)}
        onDragLeave={() => onDragLeave()}
        onDrop={(e) => onDrop(e, quadrant.id)}
      >
        <AnimatePresence mode="popLayout">
          {dropPosition?.quadrantId === quadrant.id && dropPosition.index === 0 && (
            <div className="drop-indicator" key="drop-0"></div>
          )}
          {activeTasks.map((task, activeIndex) => {
            return (
              <React.Fragment key={task.id}>
                {dropPosition?.quadrantId === quadrant.id && dropPosition.index === activeIndex + 1 && (
                  <div className="drop-indicator"></div>
                )}
                <TaskItem
                  task={task}
                  quadrantId={quadrant.id}
                  isEditing={editingTask?.quadrantId === quadrant.id && editingTask?.taskId === task.id}
                  editingInput={inputs[`edit-task-${task.id}`]}
                  onEditStart={onEditStart}
                  onEditChange={(value) => onEditChange(task.id, value)}
                  onEditSave={onEditSave}
                  onToggle={onToggleTask}
                  onDelete={onRemoveTask}
                  onDragStart={() => onDragStart(quadrant.id, task.id)}
                  onDragEnd={onDragEnd}
                  isDragging={draggedTask?.taskId === task.id}
                  onTogglePlanToday={onTogglePlanToday}
                />
              </React.Fragment>
            );
          })}
        </AnimatePresence>
      </div>

      {completedTasks.length > 0 && (
        <div className="completed-section">
          <motion.div layout key="completed-toggle" className="completed-toggle-wrapper">
            <button 
              className="completed-toggle-btn"
              onClick={() => setShowCompleted(!showCompleted)}
            >
              {showCompleted ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span>{completedTasks.length} Completed</span>
            </button>
          </motion.div>

          <AnimatePresence>
            {showCompleted && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} 
                animate={{ opacity: 1, height: 'auto' }} 
                exit={{ opacity: 0, height: 0 }}
                className="completed-task-list"
              >
                {completedTasks.map((task) => {
                  return (
                    <TaskItem
                      key={task.id}
                      task={task}
                      quadrantId={quadrant.id}
                      isEditing={editingTask?.quadrantId === quadrant.id && editingTask?.taskId === task.id}
                      editingInput={inputs[`edit-task-${task.id}`]}
                      onEditStart={onEditStart}
                      onEditChange={(value) => onEditChange(task.id, value)}
                      onEditSave={onEditSave}
                      onToggle={onToggleTask}
                      onDelete={onRemoveTask}
                      onDragStart={() => {}} // Disable dragging for completed
                      onDragEnd={() => {}}
                      isDragging={false}
                      onTogglePlanToday={onTogglePlanToday}
                    />
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      <div className="task-input-container">
        <input
          type="text"
          placeholder="Add a task..."
          value={inputs[quadrant.id] || ''}
          onChange={(e) => {
            setInputs((prev) => ({ ...prev, [quadrant.id]: e.target.value }));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onAddTask(quadrant.id);
            }
          }}
        />
        <span className="input-icon">
          <Plus size={18} />
        </span>
      </div>
    </motion.div>
  );
}
